import * as z from 'zod'

export const PresetEnum = z.enum(['by_warehouse', 'by_material', 'by_project'])
export type Preset = z.infer<typeof PresetEnum>

export const ItemKindEnum = z.enum(['material_raw', 'material_remnant', 'part', 'assembly'])
export type ItemKind = z.infer<typeof ItemKindEnum>

// Report view (column/row preset): movements = full ОСВ, balances = closing only,
// consumption = issue only. Lives here with the other zod schemas.
export const ViewEnum = z.enum(['movements', 'balances', 'consumption'])
export type ReportView = z.infer<typeof ViewEnum>

// Pieces, or the physical measure next to them (m / m² / kg).
export const UnitsEnum = z.enum(['pieces', 'measure'])
export type ReportUnits = z.infer<typeof UnitsEnum>

export const GroupTypeEnum = z.enum([
  'warehouse',
  'project',
  'nomenclature_group',
  'material',
  'part',
  'assembly',
  'document',
])
export type GroupType = z.infer<typeof GroupTypeEnum>

// Filter (query params)
export const StockMovementsFilterSchema = z
  .object({
    period_from: z.string(), // ISO date "YYYY-MM-DD"
    period_to: z.string(),
    preset: PresetEnum.default('by_warehouse'),
    project_ids: z.array(z.number()).nullable().optional(),
    warehouse_ids: z.array(z.number()).nullable().optional(),
    item_kinds: z.array(ItemKindEnum).nullable().optional(),
    group_ids: z.array(z.number()).nullable().optional(),
  })
  .refine((v) => v.period_to >= v.period_from, {
    message: 'period_to must be >= period_from',
    path: ['period_to'],
  })

export type StockMovementsFilter = z.infer<typeof StockMovementsFilterSchema>

// Row (output)
export const StockMovementsRowSchema = z.object({
  level: z.number().int().nonnegative(),
  group_type: GroupTypeEnum,
  group_id: z.number().nullable(),
  label: z.string(),
  unit_code: z.string().nullable(),
  // Decimal from Pydantic serializes as string
  opening_balance: z.string(),
  receipt_qty: z.string(),
  issue_qty: z.string(),
  // What of issue_qty went to the shop on issue notes; transfers excluded.
  consumed_qty: z.string(),
  closing_balance: z.string(),
  drill_url: z.string().nullable(),
  // Physical measure: m (profile), m² (sheet), kg (parts, marks). Null when the
  // row mixes measure units or a size / weight is missing.
  measure_unit: z.string().nullable().optional(),
  opening_measure: z.string().nullable().optional(),
  receipt_measure: z.string().nullable().optional(),
  issue_measure: z.string().nullable().optional(),
  consumed_measure: z.string().nullable().optional(),
  closing_measure: z.string().nullable().optional(),
})

export type StockMovementsRow = z.infer<typeof StockMovementsRowSchema>

// Report envelope
export const StockMovementsReportOutSchema = z.object({
  // Django-Ninja serialises PeriodOut without by_alias, so the key is `from_` not `from`.
  period: z.object({ from_: z.string(), to: z.string() }),
  preset: PresetEnum,
  rows: z.array(StockMovementsRowSchema),
  generated_at: z.string(), // ISO datetime
})

export type StockMovementsReportOut = z.infer<typeof StockMovementsReportOutSchema>
